import {FC, useCallback, useEffect, useState} from "react"

import {getHostBridge} from "@frontend/hostContract"
import {useSession} from "@frontend/session"
import {
  addItemsToCart,
  buildPartCartItem,
  getHostPartPrice,
  getHostPartPriceAndStockStatus,
  navigateToPart,
  toPartPriceRequestItem,
} from "@frontend/utils/appHostFunctions"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useAddToCartMutation} from "@frontend/utils/trpc"
import {CauseOnMessageWithCauseAndPart} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

interface Props {
  cause: CauseOnMessageWithCauseAndPart
  caseId: string
  modelNumber: string | null
  compact?: boolean
}

/**
 * Compact card that surfaces the required part for a confirmed cause.
 * Designed for the chat widget on ecommerce pages to drive part sales.
 */
export const PartCard: FC<Props> = ({
  cause,
  caseId,
  modelNumber,
  compact = false,
}) => {
  const {part} = cause
  const {userRcId} = useSession()
  const addToCartMutation = useAddToCartMutation()
  const [hostPrice, setHostPrice] = useState<number | undefined>()
  const [hostStockStatus, setHostStockStatus] = useState<Ri2PartStockStatus>()

  useEffect(() => {
    if (!part) {
      setHostPrice(undefined)
      setHostStockStatus(undefined)
      return
    }

    let isMounted = true
    setHostPrice(undefined)
    setHostStockStatus(undefined)

    void getHostPartPriceAndStockStatus(
      toPartPriceRequestItem({
        oemNumber: part.manufacturerPartNumber,
        manufacturerName: part.manufacturerName ?? "",
      }),
    ).then(({price, stockStatus}) => {
      if (!isMounted) return
      setHostPrice(typeof price === "number" ? price : undefined)
      setHostStockStatus(stockStatus)
    })

    return (): void => {
      isMounted = false
    }
  }, [part, modelNumber])

  const onAddToCart = useCallback(async () => {
    if (!part) return null

    trackEvent(compact ? "widget_add_to_cart" : "chat_add_to_cart")

    const price = await getHostPartPrice(
      toPartPriceRequestItem({
        oemNumber: part.manufacturerPartNumber,
        manufacturerName: part.manufacturerName ?? "",
      }),
    )

    addItemsToCart(
      [
        buildPartCartItem({
          id: part.id,
          oemNumber: part.manufacturerPartNumber,
          name: part.name ?? undefined,
          price: typeof price === "number" ? price : undefined,
        }),
      ],
      caseId,
    )

    addToCartMutation.mutate({
      userRcId,
      caseId,
      partId: part.id,
    })
  }, [part, caseId, modelNumber, addToCartMutation, userRcId, compact])

  const onViewPart = useCallback(() => {
    if (!part) return null

    trackEvent(compact ? "widget_view_part" : "chat_view_part")
    navigateToPart(part, {caseId})
  }, [part, caseId, compact])
  if (!part) return null

  const hasCartIntegration = !!getHostBridge()?.addItemsToCart
  // Navigate is possible when the host provides a URL builder OR a purchaseUrl exists
  const canNavigate = !!getHostBridge()?.getPartDetailUrl || !!part.purchaseUrl
  const formattedPrice =
    typeof hostPrice === "number" ? `$${hostPrice.toFixed(2)}` : null
  const normalizedHostStockStatus = hostStockStatus?.trim().toLowerCase()
  const isOutOfStockOnHost =
    normalizedHostStockStatus === "out_of_stock" ||
    normalizedHostStockStatus === "out-of-stock" ||
    normalizedHostStockStatus === "unavailable"

  return (
    <div
      className={css({
        border: "1px solid token(colors.border.secondary.200)",
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: "white",
        maxWidth: 320,
      })}
    >
      {/* Part image */}
      {part.image && (
        <div
          onClick={canNavigate ? onViewPart : undefined}
          className={css({
            width: "100%",
            height: 120,
            overflow: "hidden",
            backgroundColor: "background.subtle.primary",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: canNavigate ? "pointer" : undefined,
          })}
        >
          <img
            src={part.image.url}
            alt={part.name ?? "Part"}
            className={css({
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            })}
          />
        </div>
      )}

      {/* Part details */}
      <div
        className={vstack({
          padding: 12,
          gap: 6,
          alignItems: "stretch",
        })}
      >
        {/* Part name */}
        <p
          onClick={canNavigate ? onViewPart : undefined}
          className={css({
            color: "text.primary",
            textStyle: "captionMedium",
            fontWeight: 600,
            lineClamp: 2,
            margin: 0,
            cursor: canNavigate ? "pointer" : undefined,
            _hover: canNavigate ? {textDecoration: "underline"} : undefined,
          })}
        >
          {part.name ?? cause.cause.name}
        </p>

        {/* Part number */}
        <p
          className={css({
            color: "text.tertiary",
            fontSize: 11,
            margin: 0,
          })}
        >
          {"Part #"}
          {part.manufacturerPartNumber}
        </p>

        {/* Price + CTA row */}
        <div
          className={hstack({
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
          })}
        >
          {formattedPrice && (
            <span
              className={css({
                color: "text.primary",
                fontWeight: 700,
                fontSize: 16,
              })}
            >
              {formattedPrice}
            </span>
          )}

          {hasCartIntegration ? (
            <button
              onClick={onAddToCart}
              className={css({
                backgroundColor: "background.brand.accent",
                color: "foreground.primaryOnSolid",
                border: "none",
                borderRadius: 8,
                paddingX: 14,
                paddingY: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flex: formattedPrice ? undefined : 1,
                _hover: {
                  opacity: 0.9,
                },
              })}
            >
              {"Add to Cart"}
            </button>
          ) : canNavigate ? (
            <button
              onClick={onViewPart}
              className={css({
                backgroundColor: "background.brand.accent",
                color: "foreground.primaryOnSolid",
                border: "none",
                borderRadius: 8,
                paddingX: 14,
                paddingY: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
                flex: formattedPrice ? undefined : 1,
                _hover: {
                  opacity: 0.9,
                },
              })}
            >
              {"View Part"}
            </button>
          ) : null}
        </div>

        {(part.isNoLongerAvailable || isOutOfStockOnHost) && (
          <p
            className={css({
              color: "text.error.primary",
              fontSize: 11,
              fontWeight: 500,
              margin: 0,
            })}
          >
            {"Currently unavailable"}
          </p>
        )}
      </div>
    </div>
  )
}
